import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

import { SchemaOrg } from '@/utils/seo';

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    {
      question: 'How does registration work?',
      answer: 'Once registration is open, members are selected on a first-come, first-served basis. Payment is taken at time of registration, but refunds are issued automatically if the event sells out. Selected members are notified by email when they have been accepted to play. Once the event sells out, additional members are placed on a waitlist. Waitlisted members are chosen by lottery if spots become available due to cancellations.'
    },
    {
      question: 'What is the cancellation policy?',
      answer: 'Cancellations are allowed up to one week before the event. Refunds are processed once the spot is filled, minus a 5% processing fee. Please email refund requests to chris.leary@bucketlistgolf.org.'
    },
    {
      question: 'Who can sign up for the events?',
      answer: 'Events are first open to Bucket List Series members on Thousand Greens. If spots remain, registration opens to the public (including Broken Tee Society and other golf groups). Members are welcomed to sign up multiple players, but members must be registered on the Bucket List Series website on Golf Genius here. Every effort will be made to pair groups together when registering multiple players.'
    },
    {
      question: 'What\'s included in the event fees?',
      answer: 'Each event is custom-curated for the venue and the event. Event fees usually include greens fees, cart/caddie, meals, tee gifts, pro shop credit, breakfast, lunch, or dinner receptions, etc. Some events may include travel and lodging. Full details are listed in the \'What\'s Included\' section on the Bucket List Series Golf Genius website.'
    },
    {
      question: 'What happens if the event is canceled due to weather?',
      answer: 'Weather cancellations are determined at the sole discretion of the golf course. Refunds will be provided whenever applicable. If the event proceeds in extremely bad weather, impacted members will receive first right of refusal for the next year\'s event at that location.'
    },
    {
      question: 'Are singles welcomed?',
      answer: 'Absolutely! Come as a group, or come as a single and meet other new friends from BLS.'
    },
    {
      question: 'Are there handicap or skill requirements?',
      answer: 'Players of all skill levels and handicaps are welcome to register. Pace of play is very important, so we just ask you to keep up with the group in front of you. Double-bogey max scoring will be in place to ensure pace of play. A GHIN or other accredited handicap is required to compete in the NET division.'
    },
    {
      question: 'What is not included in the registration fees?',
      answer: 'Airfare and lodging are not included in these fees. Occasionally, we may offer discounted rooming blocks where applicable, but those costs will typically be separate from the registration fees. Details for each event will be included in the Bucket List Series Golf Genius website for the given event.'
    }
  ];

  const faqSchema = {
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <SchemaOrg type="FAQPage" data={faqSchema} />
      <section id="faq" className="py-16 sm:py-20 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 leading-tight px-2">
              Frequently Asked Questions
            </h2>
            <p className="text-base sm:text-lg text-gray-600 px-4">
              Find answers to common questions about the event
            </p>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200 hover:border-emerald-300 transition-colors"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <span className="text-base sm:text-lg font-semibold text-gray-900 pr-4 leading-tight">
                    {faq.question}
                  </span>
                  <i
                    className={`${
                      openIndex === index ? 'ri-subtract-line' : 'ri-add-line'
                    } text-xl sm:text-2xl text-emerald-600 flex-shrink-0`}
                  ></i>
                </button>
                {openIndex === index && (
                  <div className="px-4 sm:px-6 pb-4 sm:pb-5">
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-10 sm:mt-12 text-center p-6 sm:p-8 bg-emerald-50 rounded-lg">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3 leading-tight">
              Still have questions?
            </h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">
              Our team is here to help! Contact us for more information.
            </p>
            <a
              href="#contact"
              className="inline-block bg-emerald-600 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-md text-sm sm:text-base font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
